import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/data/turboData";
import { SERVICES as IDS } from "@/constants/testIds";

const icons = {
  "turbo-air-express": (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 16h22l-4-6h-4l-2 3H8l-3-3H3z" />
      <path d="M9 22l4 4h2l-1-4" />
      <path d="M27 12l3-3M27 16l3 0M27 20l3 3" />
    </svg>
  ),
  "turbo-bulk-express": (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="22" height="14" rx="1.5" />
      <path d="M5 16h22M16 11v14" />
    </svg>
  ),
  "standard-international-shipping": (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="16" cy="16" r="11" />
      <path d="M5 16h22M16 5c3 3 5 7 5 11s-2 8-5 11M16 5c-3 3-5 7-5 11s2 8 5 11" />
    </svg>
  ),
  "travel-bags": (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="7" y="11" width="18" height="16" rx="2" />
      <path d="M12 11V8a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3M11 16v6M21 16v6" />
    </svg>
  ),
  "customer-bulk-shipping": (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="16" cy="9" rx="9" ry="3" />
      <path d="M7 9v14c0 1.7 4 3 9 3s9-1.3 9-3V9" />
      <path d="M7 16c0 1.7 4 3 9 3s9-1.3 9-3" />
    </svg>
  ),
  "ocean-freight": (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 22c2 1 4 1 6 0s4-1 6 0 4 1 6 0 4-1 6 0" />
      <path d="M4 26c2 1 4 1 6 0s4-1 6 0 4 1 6 0 4-1 6 0" />
      <path d="M6 20l2-8h16l2 8" />
      <path d="M16 4v8M12 8h8" />
    </svg>
  ),
  "rail-freight": (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="7" y="6" width="18" height="18" rx="3" />
      <path d="M7 15h18" />
      <circle cx="12" cy="20" r="1.2" />
      <circle cx="20" cy="20" r="1.2" />
      <path d="M11 24l-2 3M21 24l2 3" />
      <path d="M2 28h28" />
    </svg>
  ),
};

const ServicesSection = () => (
  <section className="relative py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-accent)] font-semibold">
            What we ship
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 max-w-2xl leading-[1.05]">
            Seven ways to move your stuff across the world.
          </h2>
        </div>
        <Link
          to="/services"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--tt-navy)] hover:text-[var(--tt-accent)] transition self-start md:self-auto"
        >
          Compare all services <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => (
          <article
            key={s.slug}
            data-testid={IDS.card(s.slug)}
            className={`card p-7 flex flex-col ${i === 0 ? "lg:row-span-2 bg-[var(--tt-navy)] text-white border-[var(--tt-navy)]" : ""}`}
          >
            <div className="flex items-center justify-between">
              <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${
                i === 0 ? "bg-[var(--tt-accent)]/20 text-[var(--tt-accent-2)]" : "bg-[var(--tt-navy)]/5 text-[var(--tt-navy)]"
              }`}>
                {icons[s.slug]}
              </div>
              {s.badge && (
                <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold rounded-full px-2.5 py-1 ${
                  i === 0 ? "bg-[var(--tt-accent)] text-white" : "bg-[var(--tt-navy)] text-white"
                }`}>
                  {s.badge}
                </span>
              )}
            </div>

            <h3 className={`font-display text-2xl font-bold mt-5 ${i === 0 ? "text-white" : "text-[var(--tt-navy)]"}`}>
              {s.name}
            </h3>
            <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-white/80" : "text-[var(--tt-ink-soft)]"}`}>
              {i === 0 ? s.long : s.short}
            </p>

            <div className={`mt-5 flex items-center gap-4 text-xs ${i === 0 ? "text-white/70" : "text-[var(--tt-muted)]"}`}>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent)]" />
                {s.eta}
              </span>
              <span className={`font-semibold ${i === 0 ? "text-[var(--tt-accent-2)]" : "text-[var(--tt-accent)]"}`}>
                {s.startsAt}
              </span>
            </div>

            {i === 0 && (
              <ul className="mt-6 space-y-2.5">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-white/90">
                    <CheckCircle2 className="h-4 w-4 text-[var(--tt-accent-2)] mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-6 pt-5 border-t border-current/10 flex items-center justify-between">
              <Link
                to="/quote"
                data-testid={IDS.cta(s.slug)}
                className={`text-sm font-semibold inline-flex items-center gap-1 transition ${
                  i === 0 ? "text-white hover:text-[var(--tt-accent-2)]" : "text-[var(--tt-navy)] hover:text-[var(--tt-accent)]"
                }`}
              >
                Get a quote <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/services"
                className={`text-sm ${i === 0 ? "text-white/60 hover:text-white" : "text-[var(--tt-muted)] hover:text-[var(--tt-navy)]"} transition`}
              >
                Learn more
              </Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default ServicesSection;
